import * as Accordion from "#root/components/accordion";
import { GlyphLoading } from "#root/components/glyphs";
import { WithSidebar } from "#root/components/withSidebar";
import { getMonthlyUsageCollectionForDeployment } from "#root/schema/usage";
import { useData } from "#root/utility/hooks";
import { monthNames } from "#root/utility/months";
import formatNumber from "cloudquote-number-format";
import type { ReactNode } from "react";
import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import PageLayout from "./layout";

export default function UsagePage({ ymd, deploymentId }): ReactNode {
  const months = useMemo(() => {
    const months: Array<{ name: string; ymd: string }> = [];
    const date = new Date();
    date.setUTCDate(1);
    date.setUTCHours(0, 0, 0, 0);
    const start = new Date("2016-08-01");

    for (let i = 0; i < 12 && date > start; i++) {
      const ymd = date.toISOString().replace(/T.*/, "");
      months.push({
        name: monthNames[date.getUTCMonth()] + " " + date.getUTCFullYear(),
        ymd,
      });
      date.setUTCMonth(date.getUTCMonth() - 1);
    }

    return months;
  }, []);

  return (
    <PageLayout title="Usage">
      <WithSidebar>
        <UsageSidebar months={months} ymd={ymd} deploymentId={deploymentId}/>
        <section>
          {ymd ? (
            <UsageTable ymd={ymd} deploymentId={deploymentId}/>
          ) : (
            <Fragment>
              <h1>API Usage</h1>
              <p>Select a month from the sidebar to view a detailed breakdown of your API requests by product and unit.</p>
            </Fragment>
          )}
        </section>
      </WithSidebar>
    </PageLayout>
  );
}

function UsageSidebar({ ymd, months, deploymentId }: { ymd: string; months: Array<{ name: string; ymd: string }>; deploymentId: string }) {
  return (
    <aside>
      <Accordion.Parent initialItemId={ymd} initialGroupId="By Month">
        <Accordion.Group label="By Month">
          {months.map(month => (
            <Accordion.Item key={month.ymd} itemId={month.ymd}>
              <Link to={`/account/deployment/${deploymentId}/usage/month/${month.ymd}`}>{month.name}</Link>
            </Accordion.Item>
          ))}
        </Accordion.Group>
      </Accordion.Parent>
    </aside>
  );
}

function UsageTable({ ymd, deploymentId }: { ymd: string; deploymentId: string }) {
  const [details] = useData(async () => {
    if (ymd) {
      const { rows } = await getMonthlyUsageCollectionForDeployment("self", deploymentId, ymd).list();

      return rows
        .filter(row => !row.unit.startsWith("Error"))
        .sort((a, b) => {
          if (a.name == null) {
            return -1;
          }

          if (b.name == null) {
            return 1;
          }

          if (a.name === b.name) {
            if (a.unit == null) {
              return -1;
            }

            return a.unit.localeCompare(b.unit);
          }

          return a.name.localeCompare(b.name);
        });
    }
  }, [deploymentId, ymd]);

  if (!details) {
    return <GlyphLoading/>;
  }

  const date = new Date(ymd);

  return (
    <div className="usage-table">
      <h1>
        {monthNames[date.getUTCMonth()]} {date.getUTCFullYear()} - API Requests
      </h1>
      <table className="table table-striped">
        <thead>
        <tr>
          <th>Product Code</th>
          <th>Unit</th>
          <th>Count</th>
        </tr>
        </thead>
        <tbody>
        {details.map((row, i) => (
          <tr key={`${ymd}-${i}`}>
            <td>{row.name}</td>
            <td>{row.unit}</td>
            <td>{formatNumber(row.requests, { Comma: true })}</td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
}
